export const getFieldValue = (source, key, ehrType) => {
  let value = source?.[key];
  let url, error;

  if (Array.isArray(value)) {
    const isHistoryArray =
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      ("value" in value[0] || "is_completed" in value[0]);

    if (isHistoryArray) {
      const draftRecord =
        ehrType === "cumulative"
          ? value.filter(record => !record?.is_completed).pop()
          : value[value.length - 1];
      value = draftRecord?.value;
      url = draftRecord?.url;
      error = draftRecord?.error;
    } else {
      return { value: value ?? [], url: "", error: "" };
    }
  }

  if (typeof value === "object" && value != null) {
    error = value.error;
    value = value.value;
  }

  return { value: value ?? "", url: url ?? "", error: error ?? "" };
};

const compactObject = obj => {
  if (!obj || typeof obj !== "object") return {};
  const result = {};
  for (const key in obj) {
    const val = obj[key];
    if (val !== null && val !== undefined && val !== "") {
      if (Array.isArray(val) && val.length === 0) continue;
      result[key] = val;
    }
  }
  return result;
};

export const CT_FIELD_GROUPS = {
  COMMON_EXTRA_FIELDS: [
    "ct_socio_demographics",
    "ct_relationship_duration",
    "ct_hopi",
    "ct_presenting_complaints_hopi",
    "ct_past_history",
    "ct_relationship_family_dynamics",
    "ct_relationship_history",
    "ct_family_development_stages",
    "ct_intimacy_sexual_functioning",
    "ct_relationship_structures",
    "ct_subsystems_boundaries",
    "ct_roles",
    "ct_rules_rituals",
    "ct_power_distribution",
    "ct_relational_processes",
    "ct_emotional_expression",
    "ct_individuation",
    "ct_communication",
    "ct_ethical_legal_issues",
    "ct_risk_factors_1",
    "ct_readiness_couples_therapy",
    "ct_additional_observations",
    "ct_risk_factors_2",
  ],
  CLIENT_FIELDS: [
    "ct_presenting_problem_client",
    "ct_psychiatric_history_client",
    "ct_medical_history_client",
    "ct_family_history_client",
    "ct_history_infidelity_client",
    "ct_risk_assessment_client",
    "ct_substance_use_client",
    "ct_stress_client",
    "ct_goals_couples_client",
  ],
  PARTNER_FIELDS: [
    "ct_presenting_problem_partner",
    "ct_psychiatric_history_partner",
    "ct_medical_history_partner",
    "ct_family_history_partner",
    "ct_history_infidelity_partner",
    "ct_risk_assessment_partner",
    "ct_substance_use_partner",
    "ct_stress_partner",
    "ct_goals_couples_partner",
  ],
};

const createMappings = (fields, suffixToRemove = "") =>
  fields.map(field => ({
    inputKey: field,
    payloadKey: field.replace(/^ct_/, "").replace(suffixToRemove, ""),
  }));

const CT_COMMON_EXTRA_FIELD_MAPPINGS = createMappings(
  CT_FIELD_GROUPS.COMMON_EXTRA_FIELDS
);
const CT_CLIENT_FIELD_MAPPINGS = createMappings(
  CT_FIELD_GROUPS.CLIENT_FIELDS,
  "_client"
);
const CT_PARTNER_FIELD_MAPPINGS = createMappings(
  CT_FIELD_GROUPS.PARTNER_FIELDS,
  "_partner"
);

const createReverseMap = mappings =>
  Object.fromEntries(mappings.map(m => [m.payloadKey, m.inputKey]));

export const CT_RESPONSE_TO_INPUT_KEY_MAP = {
  ...createReverseMap(CT_COMMON_EXTRA_FIELD_MAPPINGS),
  ...createReverseMap(CT_CLIENT_FIELD_MAPPINGS),
};

export const CT_PARTNER_RESPONSE_TO_INPUT_KEY_MAP = {
  ...createReverseMap(CT_COMMON_EXTRA_FIELD_MAPPINGS),
  ...createReverseMap(CT_PARTNER_FIELD_MAPPINGS),
};

const CLIENT_TOP_LEVEL = [
  ["firstname", "firstname"],
  ["lastname", "lastname"],
  ["age", "age"],
  ["date_of_birth", "date_of_birth"],
  ["gender", "gender"],
  ["sex", "sex"],
  ["pronouns", "pronouns"],
  ["another_pronoun", "another_pronoun"],
  ["not_listed_gender", "not_listed_gender"],
  ["highest_qualification", "highest_qualification"],
  ["qualification_details", "qualification_details"],
  ["occupation", "occupation"],
  ["other_occupation", "other_occupation"],
  ["maritial_status", "maritial_status"],
  ["other_maritial_status", "other_maritial_status"],
  ["ct_relationship_status", "couple_family_type"],
  ["referral_source", "referral_source"],
  ["family_tree", "family_tree"],
  ["family_tree_url", "family_tree_url"],
  ["genogram", "genogram"],
  ["genogram_url", "genogram_url"],
];

const PARTNER_TOP_LEVEL = [
  ["partner_firstname", "firstname"],
  ["partner_lastname", "lastname"],
  ["partner_date_of_birth", "date_of_birth"],
  ["partner_gender", "gender"],
  ["partner_sex", "sex"],
  ["partner_pronouns", "pronouns"],
  ["partner_highest_qualification", "highest_qualification"],
  ["partner_qualification_details", "qualification_details"],
  ["partner_occupation", "occupation"],
  ["partner_other_occupation", "other_occupation"],
  ["partner_genogram", "genogram"],
  ["partner_genogram_url", "genogram_url"],
];

export const buildCouplePayloadDetails = ({
  sourceData,
  partnerId,
  ehrType,
  modifiedData = null,
}) => {
  const modifiedKeys =
    modifiedData && typeof modifiedData === "object"
      ? new Set(Object.keys(modifiedData))
      : null;
  const shouldInclude = key => !modifiedKeys || modifiedKeys.has(key);

  const processFields = mappings => {
    const target = {};
    for (const [inputKey, payloadKey] of mappings) {
      if (shouldInclude(inputKey)) {
        const { value } = getFieldValue(sourceData, inputKey, ehrType);
        target[payloadKey] = value;
      }
    }
    return target;
  };

  const clientDetails = {
    ...processFields(CLIENT_TOP_LEVEL),
    ...processFields(
      CT_COMMON_EXTRA_FIELD_MAPPINGS.map(m => [m.inputKey, m.payloadKey])
    ),
    ...processFields(
      CT_CLIENT_FIELD_MAPPINGS.map(m => [m.inputKey, m.payloadKey])
    ),
  };

  const partnerDetails = {
    ...processFields(PARTNER_TOP_LEVEL),
    ...processFields(
      CT_PARTNER_FIELD_MAPPINGS.map(m => [m.inputKey, m.payloadKey])
    ),
  };

  const compactClient = compactObject(clientDetails);
  const compactPartner = compactObject(partnerDetails);

  if (partnerId != null && Object.keys(compactPartner).length > 0) {
    compactPartner.id = partnerId;
  }

  return compactObject({
    extra_details: Object.keys(compactClient).length > 0 ? compactClient : null,
    partner_extra_details:
      Object.keys(compactPartner).length > 0 ? compactPartner : null,
  });
};

export const createEhrPayload = ({
  inputData,
  isCoupleSession,
  clientId,
  partnerId,
  providerId,
  clientPartnerMappingId,
  ehrType,
  isCompleted = false,
  modifiedData = null,
}) => {
  const payloadDetails = isCoupleSession
    ? modifiedData
      ? buildCouplePayloadDetails({
          sourceData: inputData,
          partnerId,
          ehrType,
          modifiedData,
        })
      : {}
    : { extra_details: modifiedData || compactObject(inputData) };

  return {
    electronic_health_record: {
      ...(isCoupleSession && clientPartnerMappingId
        ? { client_partner_mapping_id: clientPartnerMappingId }
        : {}),
      user_id: clientId,
      provider_id: providerId,
      is_completed: isCompleted,
      ...payloadDetails,
    },
  };
};

export const mapCtPartnerDetailsToInputData = (
  partnerExtraDetails,
  clientExtraDetails,
  ehrType
) => {
  const extract = (source, key) => getFieldValue(source, key, ehrType).value;

  const relationshipStatus = extract(clientExtraDetails, "couple_family_type");

  const result = {
    partner_firstname: extract(partnerExtraDetails, "firstname"),
    partner_lastname: extract(partnerExtraDetails, "lastname"),
    partner_date_of_birth: extract(partnerExtraDetails, "date_of_birth"),
    partner_gender: extract(partnerExtraDetails, "gender"),
    partner_sex: extract(partnerExtraDetails, "sex"),
    partner_pronouns: extract(partnerExtraDetails, "pronouns"),
    partner_highest_qualification: extract(
      partnerExtraDetails,
      "highest_qualification"
    ),
    partner_qualification_details: extract(
      partnerExtraDetails,
      "qualification_details"
    ),
    partner_occupation: extract(partnerExtraDetails, "occupation"),
    partner_other_occupation: extract(partnerExtraDetails, "other_occupation"),
    ct_relationship_status: relationshipStatus,
    partner_genogram: extract(partnerExtraDetails, "genogram"),
    partner_genogram_url: extract(partnerExtraDetails, "genogram_url"),
  };

  CT_COMMON_EXTRA_FIELD_MAPPINGS.forEach(({ inputKey, payloadKey }) => {
    result[inputKey] = extract(clientExtraDetails, payloadKey);
  });

  CT_CLIENT_FIELD_MAPPINGS.forEach(({ inputKey, payloadKey }) => {
    result[inputKey] = extract(clientExtraDetails, payloadKey);
  });

  CT_PARTNER_FIELD_MAPPINGS.forEach(({ inputKey, payloadKey }) => {
    result[inputKey] = extract(partnerExtraDetails, payloadKey);
  });
  result.ct_relationship_status = relationshipStatus;
  console.log("Mapped CT Partner Details to Input Data:", result);
  return result;
};
